import React from 'react'
import {HashRouter as Router, Routes, Route, Navigate} from 'react-router-dom'
import {Toaster} from 'react-hot-toast'
import {HelmetProvider} from 'react-helmet-async'
import {AuthProvider, useAuth} from './contexts/AuthContext'
import {BrandingProvider} from './components/BrandingProvider'
import Layout from './components/layout/Layout'
import AdminLayout from './components/layout/AdminLayout'
import LoadingSpinner from './components/ui/LoadingSpinner'

// Pages
import Home from './pages/Home'
import Courses from './pages/Courses'
import CourseDetail from './pages/CourseDetail'
import CoursePlayer from './pages/CoursePlayer'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import Dashboard from './pages/Dashboard'
import CheckoutSuccess from './pages/checkout/CheckoutSuccess'
import Contact from './pages/Contact'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard'
import CourseManagement from './pages/admin/CourseManagement'
import CourseEditor from './pages/admin/CourseEditor'
import BulkImport from './pages/admin/BulkImport'
import BrandingSettings from './components/admin/BrandingSettings'
import HomepageContentManager from './components/admin/HomepageContentManager'
import SiteContentManager from './pages/admin/SiteContentManager'

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {hasError: false, error: null}
  }

  static getDerivedStateFromError(error) {
    return {hasError: true, error}
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Connection Error Component
const ConnectionError = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-6">
      <div className="text-red-600 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Connection Error</h1>
      <p className="text-gray-600 mb-6">
        Unable to connect to the database. Please check your internet connection and try again.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
      >
        Retry Connection
      </button>
    </div>
  </div>
)

// Protected Route Component
const ProtectedRoute = ({children, adminOnly = false}) => {
  const {user, profile, loading, connectionError} = useAuth()

  if (connectionError) {
    return <ConnectionError />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (adminOnly && profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({children}) => {
  const {user, loading, connectionError} = useAuth()

  if (connectionError) {
    return <ConnectionError />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <BrandingProvider>
            <Router>
              <div className="App">
                <Routes>
                  {/* Public routes with Layout (Header + Footer) */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="courses" element={<Courses />} />
                    <Route path="courses/:slug" element={<CourseDetail />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="privacy" element={<PrivacyPolicy />} />
                    <Route path="terms" element={<TermsOfService />} />
                  </Route>

                  {/* Auth routes (no header/footer) */}
                  <Route path="/login" element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  } />
                  <Route path="/register" element={
                    <PublicRoute>
                      <Register />
                    </PublicRoute>
                  } />

                  {/* Protected routes with Layout */}
                  <Route path="/" element={<Layout />}>
                    <Route path="dashboard" element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                  </Route>

                  {/* Course Player Routes (no header/footer for immersive experience) */}
                  <Route path="/course/:courseSlug" element={
                    <ProtectedRoute>
                      <CoursePlayer />
                    </ProtectedRoute>
                  } />
                  <Route path="/course/:courseSlug/lesson/:lessonId" element={
                    <ProtectedRoute>
                      <CoursePlayer />
                    </ProtectedRoute>
                  } />

                  {/* Admin Routes with AdminLayout (Header + Footer) */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={
                      <ProtectedRoute adminOnly={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="courses" element={
                      <ProtectedRoute adminOnly={true}>
                        <CourseManagement />
                      </ProtectedRoute>
                    } />
                    <Route path="courses/:courseId" element={
                      <ProtectedRoute adminOnly={true}>
                        <CourseEditor />
                      </ProtectedRoute>
                    } />
                    <Route path="courses/:courseId/bulk-import" element={
                      <ProtectedRoute adminOnly={true}>
                        <BulkImport />
                      </ProtectedRoute>
                    } />
                    <Route path="content" element={
                      <ProtectedRoute adminOnly={true}>
                        <SiteContentManager />
                      </ProtectedRoute>
                    } />
                    <Route path="branding" element={
                      <ProtectedRoute adminOnly={true}>
                        <BrandingSettings />
                      </ProtectedRoute>
                    } />
                    <Route path="homepage" element={
                      <ProtectedRoute adminOnly={true}>
                        <HomepageContentManager />
                      </ProtectedRoute>
                    } />
                  </Route>

                  {/* Checkout routes with Layout */}
                  <Route path="/" element={<Layout />}>
                    <Route path="checkout/success" element={
                      <ProtectedRoute>
                        <CheckoutSuccess />
                      </ProtectedRoute>
                    } />
                  </Route>

                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      style: {
                        background: '#10B981',
                      },
                    },
                    error: {
                      style: {
                        background: '#EF4444',
                      },
                    },
                  }}
                />
              </div>
            </Router>
          </BrandingProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App